import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, UserPlus, Lock, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { calculatePasswordStrength } from '@/lib/encryption';

const SignupForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Masterkey removed: vault will be encrypted with the account password
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const passwordStrength = calculatePasswordStrength(password);
  // No separate masterkey strength

  const getStrengthColor = (strength: 'weak' | 'medium' | 'strong') => {
    switch (strength) {
      case 'weak': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'strong': return 'bg-green-500';
      default: return 'bg-red-500';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword || !displayName) {
      toast({
        title: "Campos em falta",
        description: "Preencha todos os campos",
        variant: "destructive"
      });
      return;
    }
    
    if (password !== confirmPassword) {
      toast({
        title: "As palavras-passe não correspondem",
        description: "Certifique-se de que as suas palavras-passe correspondem",
        variant: "destructive"
      });
      return;
    }
    
    // No masterkey confirmation required
    
    if (passwordStrength === 'weak') {
      toast({
        title: "Palavra-passe fraca",
        description: "Utilize uma palavra-passe mais forte.",
        variant: "destructive"
      });
      return;
    }
    
    // No masterkey strength check
    
    try {
      setLoading(true);
      const userCredential = await signUp(email, password, displayName);
      
      if (userCredential) {
        toast({
          title: "Conta criada",
          description: "A sua conta foi criada com sucesso.",
          variant: "default"
        });
        navigate('/unlock');
      }
    } catch (error: any) {
      setLoading(false);
      toast({
        title: "Falha no registo",
        description: error.message || "Ocorreu um erro durante o registo",
        variant: "destructive"
      });
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1">
        <div className="flex justify-center mb-4">
          <Shield className="h-12 w-12 text-primary" />
        </div>
        <CardTitle className="text-2xl font-bold text-center">Criar conta</CardTitle>
        <CardDescription className="text-center">
          Crie uma conta para começar a gerir as suas palavras-passe
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="nome@exemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="displayName">Nome de exibição</Label>
            <Input
              id="displayName"
              type="text"
              placeholder="O seu nome"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">Palavra-passe</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="font-lato"
            />
            {password && (
              <div className="h-1 w-full bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${getStrengthColor(passwordStrength)}`} 
                  style={{ width: passwordStrength === 'weak' ? '33%' : passwordStrength === 'medium' ? '66%' : '100%' }}
                ></div>
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirmar palavra-passe</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="font-lato"
            />
          </div>
          
          {/* Masterkey fields removed: vault uses account password for encryption */}
          
          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center">
                <span className="animate-spin mr-2">
                  <Lock size={18} />
                </span>
                Criando conta...
              </div>
            ) : (
              <div className="flex items-center">
                <UserPlus size={18} className="mr-2" />
                Criar conta
              </div>
            )}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex flex-col space-y-2">
        <div className="text-sm text-center text-muted-foreground">
          Já tem uma conta?{" "}
          <Button 
            variant="link" 
            className="p-0 h-auto" 
            onClick={() => navigate('/login')}
          >
            Iniciar sessão
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default SignupForm;
